import { z } from "zod";

export const TransactionSources = {
  API: "api",
  SFTP: "sftp",
  EMAIL: "email",
  IOT: "iot",
  KAFKA: "kafka",
  UPLOAD: "upload",
  UNKNOWN: "unknown",
} as const;

export type TransactionSourceType =
  (typeof TransactionSources)[keyof typeof TransactionSources];

export const TransactionSteps = {
  RECORDER: "transaction_recorder",
  VULNERABILITY_SCANNER: "vulnerability_scanner",
  ARCHIVER: "archiver", // also validates
  VALIDATOR_EMAIL: "email_validator",
  OPERATOR: "operator",
  SINK: "sink",
} as const;
export type TransactionStepType =
  (typeof TransactionSteps)[keyof typeof TransactionSteps];

export const TransactionStatus = {
  PENDING: "pending",
  FAILED: "failed",
  COMPLETED: "completed",
  COMPLETED_WITH_PARTIAL_SUCCESS: "completed_with_partial_success",
} as const;

export type TransactionStatusType =
  (typeof TransactionStatus)[keyof typeof TransactionStatus];

export const DatasetTransactionStatus = {
  PENDING: "pending",
  FAILED: "failed",
  COMPLETED: "completed",
  COMPLETED_WITH_PARTIAL_SUCCESS: "completed_with_partial_success",
} as const;

export type DatasetTransactionStatusType =
  (typeof DatasetTransactionStatus)[keyof typeof DatasetTransactionStatus];

export const DatasetTransactionStatusSchema = z.nativeEnum(
  DatasetTransactionStatus
);

export const TransactionStepStates = {
  PENDING: "pending",
  SUCCESS: "success",
  SUCCESS_PARTIAL: "partial_success",
  FAILED: "failed",
  FAILED_PARTIAL: "partial_failure",
  RETRIED: "retried",
} as const;

export type TransactionStepStateType =
  (typeof TransactionStepStates)[keyof typeof TransactionStepStates];

export const TransactionDetailItemStatus = {
  CREATED: "created",
  UPDATED: "updated",
  // this status only shows up in the "archiver" step
  SUCCESSFUL: "successful",
  FAILED: "failed",
  UNCHANGED: "unchanged",
} as const;
export type TransactionDetailItemStatusType =
  (typeof TransactionDetailItemStatus)[keyof typeof TransactionDetailItemStatus];

export const TransactionStatusCountsSchema = z
  .object({
    [TransactionDetailItemStatus.CREATED]: z.number(),
    [TransactionDetailItemStatus.UPDATED]: z.number(),
    [TransactionDetailItemStatus.SUCCESSFUL]: z.number(),
    [TransactionDetailItemStatus.FAILED]: z.number(),
    [TransactionDetailItemStatus.UNCHANGED]: z.number(),
  })
  .partial();

export const TransactionSchema = z
  .object({
    transaction_id: z.string(),
    client_id: z.string(),
    dataset_id: z.string(),
    source: z.enum([
      TransactionSources.API,
      TransactionSources.SFTP,
      TransactionSources.EMAIL,
      TransactionSources.IOT,
      TransactionSources.KAFKA,
      TransactionSources.UPLOAD,
      TransactionSources.UNKNOWN,
    ]),
    step: z.enum([
      TransactionSteps.RECORDER,
      TransactionSteps.VULNERABILITY_SCANNER,
      TransactionSteps.ARCHIVER,
      TransactionSteps.VALIDATOR_EMAIL,
      TransactionSteps.OPERATOR,
      TransactionSteps.SINK,
    ]),
    bucket: z.string(),
    file_key: z.string(),
    original_file_name: z.string(),
    state: z.nativeEnum(TransactionStepStates),
    status: z.nativeEnum(TransactionStatus),
    details: z.string().nullable(),
    total_count: z.number(),
    successful_count: z.number(),
    status_counts: TransactionStatusCountsSchema.optional(),
    user_id: z.string(),
    created_at: z.string(),
  })
  .describe("Schema for transactions in the data platform");

export const TransactionRowSummarySchema = z.object({
  index: z.string(),
  data: z.string(),
  msg: z.string(),
});

export const TransactionSummarySchema = z.object({
  entity_type: z.string(),
  success_count: z.number(),
  failure_count: z.number(),
  details: TransactionRowSummarySchema,
});

export const ImprovedTransactionSummarySchema = z.object({
  transaction_id: z.string(),
  sink: TransactionSummarySchema,
});

export const TransactionDetailsItemErrorDetailSchema = z.object({
  field_name: z.string(),
  error_message: z.string(),
  validation_type: z.string(),
  validation_value: z.union([z.string(), z.array(z.string())]).nullable(),
});

export const TransactionDetailsItemSchema = z.object({
  index: z.string(),
  status: z.nativeEnum(TransactionDetailItemStatus),
  data: z.string(),
  error: z.string(),
  error_msg: z.string(),
  entity_type: z.string(),
  error_details: z.array(TransactionDetailsItemErrorDetailSchema),
});

export const TransactionDetailsSchema = z.object({
  success_count: z.number().int(),
  failure_count: z.number().int(),
  summary: z.record(z.string(), z.any()),
  details: z.array(TransactionDetailsItemSchema),
});

export const TransactionDetailsItemV2Schema = z.object({
  index: z.string(),
  status: z.nativeEnum(TransactionDetailItemStatus),
  data: z.string().nullable(),
  error: z.string().nullable(),
  error_msg: z.string().nullable(),
  entity_type: z.string().nullable(),
  error_count: z.number().int().nullable(),
  error_details: z.array(TransactionDetailsItemErrorDetailSchema).nullable(),
});

export const TransactionDetailsV2Schema = z.object({
  success_count: z.number().int().nullable(),
  failure_count: z.number().int().nullable(),
  summary: z.record(z.string(), z.any()).nullable(),
  details: z.array(TransactionDetailsItemV2Schema),
  page_size: z.number().int(),
  continuation_token: z.string().nullable().optional(),
  total_entries: z.number().int().nullable(),
  total_pages: z.number().int().nullable(),
});

export const TransactionGetListFilterSchema = z
  .object({
    /** Optional, otherwise defaults to 3 months before current datetime. Required in isoformat i.e. %Y%m%dT%H%M%S%z */
    submitted_after: z.string(),
    /** Optional, otherwise defaults to current datetime. Required in isoformat i.e. %Y%m%dT%H%M%S%z */
    submitted_before: z.string(),
    only_latest: z.boolean(),
  })
  .merge(
    TransactionSchema.pick({
      dataset_id: true,
      step: true,
      source: true,
      state: true,
      // REVIEW: not supported by BE yet
      status: true,
      bucket: true,
      file_key: true,
    })
  )
  .partial();

export type TransactionType = z.infer<typeof TransactionSchema>;
export type TransactionStatusCountsType = z.infer<
  typeof TransactionStatusCountsSchema
>;
export type TransactionSummaryType = z.infer<typeof TransactionSummarySchema>;
export type TransactionGetListFilterType = z.infer<
  typeof TransactionGetListFilterSchema
>;

export type TransactionDetailsType = z.infer<typeof TransactionDetailsSchema>;

export type TransactionDetailsItemType = z.infer<
  typeof TransactionDetailsItemSchema
>;

export type TransactionDetailsItemErrorDetailItemType = z.infer<
  typeof TransactionDetailsItemErrorDetailSchema
>;

export type TransactionDetailsV2Type = z.infer<
  typeof TransactionDetailsV2Schema
>;

export type TransactionDetailsItemV2Type = z.infer<
  typeof TransactionDetailsItemV2Schema
>;
